'use client';

import { c as _c } from "react/compiler-runtime";
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import type { AddPromoCodeProperties, IdentifyProperties, SelectCtaProperties, SelectNavCtaProperties, SelectPencilBannerCtaProperties, SelectRailCtaProperties, SelectStickyFooterCtaProperties, SetPasswordProperties, SubmitEmailAddressProperties } from '@src/ampli-web';
import { ampli } from '@src/ampli-web';
import type { IdentifyProperties as DigitalIdentifyProperties } from '@src/ampli-web-digital';
import { ampli as ampliDigital } from '@src/ampli-web-digital';
import { useRailItemContext } from '@src/components/Rail/RailItemContext';
import { configPublic } from '@src/config.public';
import { useEffectOnce } from '@src/hooks/useEffectOnce';
import { useSession } from '@src/hooks/useSession';
import { useUserAgent } from '@src/hooks/useUserAgent';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useAmplitudeIdentifyStore } from '@src/stores/amplitudeIdentifyStore';
import { useAnalyticsActiveStore } from '@src/stores/analyticsActiveStore';
import useGeolocationStore from '@src/stores/geolocationStore';
import { getPageGlobalProps, getPageURLProperties } from '@src/utils/analytics-helpers.util';
import type { AnalyticsTarget } from '@src/utils/analytics-target.util';
import { getAnalyticsTarget } from '@src/utils/analytics-target.util';
import { hash } from '@src/utils/hash.util';
import { log } from '@src/utils/log.util';
import { cookieName, parse } from './amplitude-helpers';
import { getBuilderPreviewMode } from './builder.util';
import { getIsServer } from './common.util';
import { getBrowserCookie, getBrowserCookieDomain, resetCookie } from './cookies.util';

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

export type { AnalyticsTarget };
export { getAnalyticsTarget };
export function useAnalyticsTarget() {
  const $ = _c(2);
  const t0 = usePathname();
  let t1;
  if ($[0] !== t0) {
    t1 = getAnalyticsTarget(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
type SharedAmpliClient = Pick<typeof ampli, 'openChat' | 'startConversation'>;
function getActiveAmpli(pathname: string): SharedAmpliClient {
  if (getAnalyticsTarget(pathname) === 'digital') {
    return ampliDigital as typeof ampliDigital & SharedAmpliClient;
  }
  return ampli;
}
type ObjectValueOrPromise<T> = T extends Record<string, any> ? { [K in keyof T]: Promise<T[K]> | T[K] } : never;
const isObjectEmpty = <T extends Record<string, any>,>(values: T): boolean => {
  return Object.keys(values ?? {}).length === 0;
};
const filterOutNullish = <T extends Record<string, any>,>(values: T): T => {
  return Object.fromEntries(Object.entries(values).filter(([_, value]) => {
    if (typeof value === 'object' && !(value instanceof Promise)) {
      return !Object.values(value).every(v => v === undefined);
    }
    return value !== undefined && value !== null && value !== '';
  })) as T;
};
const separateValues = <T extends Record<string, any>,>(values: T) => {
  const syncValues = {} as Record<string, any>;
  const asyncValues = {} as Record<string, Promise<any>>;
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof Promise) {
      asyncValues[key] = value;
    } else {
      syncValues[key] = value;
    }
  }
  return {
    syncValues,
    asyncValues
  };
};
export const useIdentify = (values: ObjectValueOrPromise<IdentifyProperties> = {},
/**
 * Extra dependencies to manually trigger the effect.
 * For example, if one of the items in the values object is a promise,
 * we need to pass an additional value that is not a promise to trigger the effect.
 */
extraDeps: unknown[] = []) => {
  const {
    live
  } = getBuilderPreviewMode();
  const {
    user
  } = useSession();
  const guid = user?.guid;
  const webActive = useAnalyticsActiveStore(state => state.webActive);
  const setProperties = useAmplitudeIdentifyStore(state => state.setProperties);
  const isValueSet = useAmplitudeIdentifyStore(state => state.isValueSet);
  const effectKey = useMemo(() => {
    return Object.entries(values).map(([key, value]) => `${key}:${value}`).join(',');
  }, [values]);
  const isValuesEmpty = useMemo(() => {
    return isObjectEmpty(values);
  }, [values]);
  useEffect(() => {
    if (!live || !webActive || isValuesEmpty) return;
    function filterOutSetValues(values: IdentifyProperties) {
      return Object.fromEntries(Object.entries(values).filter(([key, value]) => {
        return !isValueSet(key as keyof IdentifyProperties, value);
      }));
    }
    async function callIdentify() {
      const finalValue = filterOutNullish(values);
      let {
        asyncValues,
        syncValues
      } = separateValues(finalValue);
      syncValues = filterOutNullish(syncValues);
      syncValues = filterOutSetValues(syncValues);
      if (!isObjectEmpty(syncValues)) {
        ampli.identify(guid, syncValues as IdentifyProperties);
        ampli.flush();
        setProperties(syncValues);
      }
      asyncValues = filterOutNullish(asyncValues);
      if (isObjectEmpty(asyncValues)) return;
      const resolvedAsyncValues = await Promise.all(Object.values(asyncValues));
      let resolvedValues = {} as Record<string, any>;
      for (let i = 0; i < Object.keys(asyncValues).length; i++) {
        const key = Object.keys(asyncValues)[i];
        resolvedValues[key] = resolvedAsyncValues[i];
      }
      resolvedValues = filterOutNullish(resolvedValues);
      resolvedValues = filterOutSetValues(resolvedValues);
      if (isObjectEmpty(resolvedValues)) return;
      ampli.identify(guid, resolvedValues as IdentifyProperties);
      ampli.flush();
      setProperties(resolvedValues as IdentifyProperties);
    }
    callIdentify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, webActive, effectKey, isValuesEmpty, ...extraDeps]);
};

/**
 * Identify the current user on the digital Amplitude project.
 * Sets userId and sends digital-schema user properties whenever the session changes.
 * Clears userId on logout.
 */
function useDigitalIdentify() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getBuilderPreviewMode();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    live
  } = t0;
  const {
    user
  } = useSession();
  const guid = user?.guid;
  const digitalActive = useAnalyticsActiveStore(_temp);
  let t1;
  let t2;
  if ($[1] !== digitalActive || $[2] !== guid || $[3] !== user) {
    t1 = () => {
      if (!live || !digitalActive) {
        return;
      }
      if (!guid) {
        ampliDigital.client.setUserId(undefined);
        return;
      }
      ampliDigital.client.setUserId(guid);
      const properties = {
        guid,
        "membership state": user?.userMembershipState ?? undefined,
        "purchase region": user?.purchaseRegion ?? undefined,
        roles: user?.roles as [string, ...string[]] ?? undefined,
        "entitlement groups": user?.entitlements ?? undefined,
        "has bodi": user?.hasBodiAccess ?? undefined,
        "coach id": user?.coachId ? Number(user.coachId) : undefined,
        locale: user?.userLocale ?? undefined
      };
      ampliDigital.identify(guid, properties);
      ampliDigital.client.flush();
    };
    t2 = [live, digitalActive, guid, user];
    $[1] = digitalActive;
    $[2] = guid;
    $[3] = user;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
}

/**
 * Set marketing attribution properties in Amplitude.
 * Needed for custom campaign tracking keys not supported by default.
 * @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#click-id-parameters
 * @param key The key of the property to set (same as the query parameter key).
 */
function _temp(state) {
  return state.digitalActive;
}
export function useMarketingAttribution(key) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getBuilderPreviewMode();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    live
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  let t2;
  if ($[1] !== key || $[2] !== searchParams) {
    t1 = () => {
      if (!live || !ampli.isLoaded) {
        return;
      }
      const value = searchParams.get(key);
      const identifyEvent = new amplitude.Identify();
      identifyEvent.setOnce(`initial_${key}`, value || "EMPTY");
      ampli.client.identify(identifyEvent);
      if (value) {
        const identifyEvent_0 = new amplitude.Identify();
        identifyEvent_0.set(key, value);
        ampli.client.identify(identifyEvent_0);
      }
    };
    t2 = [key, live, searchParams];
    $[1] = key;
    $[2] = searchParams;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
}
const DEFAULT_TRACKING = {
  attribution: true,
  pageViews: true,
  sessions: true,
  formInteractions: false,
  fileDownloads: false
};
function loadWebAmpli({
  optOut
}: {
  optOut: boolean;
}) {
  if (ampli.isLoaded) return;
  const {
    apiKey,
    logLevel
  } = configPublic.amplitude;
  const domain = getBrowserCookieDomain();
  const ampCookieName = cookieName(apiKey, true);
  const [appVersion] = configPublic.general.appVersion.split('-');

  // Reset AMP cookie to ensure the amplitude cookie is decoded
  resetCookie(ampCookieName, `domain=${domain}; path=/`);
  ampli.load({
    client: {
      configuration: {
        appVersion,
        logLevel,
        optOut,
        sessionTimeout: 3600000,
        // 60 minutes in milliseconds
        defaultTracking: DEFAULT_TRACKING
      },
      apiKey
    }
  });
  const sessionReplayTracking = sessionReplayPlugin({
    forceSessionTracking: true,
    sampleRate: 1
  });
  ampli.client.add(sessionReplayTracking);

  // Expose amplitude globally so Digioh can send events on our behalf
  window.amplitude = ampli.client;
}
async function loadDigitalAmpli({
  optOut
}: {
  optOut: boolean;
}): Promise<void> {
  if (ampliDigital.isLoaded) return;
  const {
    apiKey,
    apiKeyDigital,
    logLevel
  } = configPublic.amplitude;
  if (!configPublic.featureFlags.onesiteEnabled || !apiKeyDigital) return;

  // amplitudeMiddleware sets the AMP_ cookie server-side, so both clients share one device ID.
  const ampCookieName = cookieName(apiKey, true);
  const ampCookieValue = getBrowserCookie(ampCookieName);
  const commerceDeviceId: string | undefined = ampCookieValue ? parse(ampCookieValue, true).device_id : undefined;
  const [appVersion] = configPublic.general.appVersion.split('-');
  await ampliDigital.load({
    client: {
      apiKey: apiKeyDigital,
      configuration: {
        appVersion,
        logLevel,
        instanceName: 'digital',
        optOut,
        deviceId: commerceDeviceId,
        sessionTimeout: 3600000,
        defaultTracking: DEFAULT_TRACKING
      }
    }
  }).promise;
}

// For events that must reach digital even where it is muted, like login/logout outside /app.
// Loads muted first so the on-init page view for a non-digital page is still dropped.
export async function forceDigitalTracking(track: () => void) {
  await loadDigitalAmpli({
    optOut: true
  });
  if (!ampliDigital.isLoaded) return;
  ampliDigital.client.setOptOut(false);
  track();
  await ampliDigital.client.flush().promise;
  ampliDigital.client.setOptOut(getAnalyticsTarget(location.pathname) !== 'digital');
}

/**
 * Initialize Amplitude SDK in the browser.
 */
export function useAnalytics() {
  const {
    live
  } = getBuilderPreviewMode();
  const {
    locale
  } = useLocale();
  const {
    user,
    anonymousId
  } = useSession();
  const geoLocation = useGeolocationStore(state => state.geoLocation);
  const [hasOptedOut, setHasOptedOut] = useState<boolean | undefined>(undefined);
  const userAgentData = useUserAgent();
  const target = useAnalyticsTarget();
  const setActive = useAnalyticsActiveStore(state_0 => state_0.setActive);
  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    async function activateAnalytics() {
      // Load the active client in its target mute state so the wrong one is never briefly live.
      if (target === 'web') {
        loadWebAmpli({
          optOut: false
        });
        if (ampli.isLoaded) ampli.client.setOptOut(false);
        if (ampliDigital.isLoaded) ampliDigital.client.setOptOut(true);
        if (!cancelled) setActive({
          webActive: true,
          digitalActive: false
        });
        return;
      }
      await loadDigitalAmpli({
        optOut: false
      });
      if (cancelled) return;

      // Only one Amplitude project should report events at a time - mute the inactive one.
      if (ampli.isLoaded) ampli.client.setOptOut(true);
      if (ampliDigital.isLoaded) ampliDigital.client.setOptOut(false);
      setActive({
        webActive: false,
        digitalActive: true
      });
    }
    void activateAnalytics();
    return () => {
      cancelled = true;
    };
  }, [live, target, setActive]);
  useIdentify({
    'do not sell': hasOptedOut
  });
  useEffect(() => {
    if (!configPublic.osano.isEnabled) return;
    function listenForOsanoDoNotSell() {
      const hasOptedOut_0 = window.Osano?.cm?.getConsent()?.OPT_OUT === 'DENY';
      setHasOptedOut(hasOptedOut_0);
      const uspapi = window?.__uspapi;
      if (typeof uspapi !== 'function') return;
      uspapi('getUSPData', 1, (uspData: any, success: boolean) => {
        if (success) {
          const hasOptedOut_1 = uspData?.uspString === '1-Y-' || uspData?.uspString === '1YY-';
          setHasOptedOut(hasOptedOut_1);
        } else {
          log.error('Osano IAB failure', uspData);
        }
      });
    }
    listenForOsanoDoNotSell();
    if (typeof window.Osano === 'function') {
      window.Osano('on', 'consent', listenForOsanoDoNotSell);
    }
  }, []);
  const firstVisit = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const TIME_KEY = 'firstVisitTime';
    const ID_KEY = 'firstVisitId';
    let timestamp = Number(localStorage?.getItem(TIME_KEY));
    let id = Number(localStorage?.getItem(ID_KEY));
    if (!timestamp) {
      timestamp = Date.now();
      localStorage?.setItem(TIME_KEY, String(timestamp));
    }
    if (!id) {
      // 10 digit random number
      id = Math.floor(Math.random() * 9000000000) + 1;
      localStorage?.setItem(ID_KEY, String(id));
    }
    return {
      timestamp,
      id
    };
  }, []);
  const userAgent = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const agent = navigator.userAgent.toString();
    return agent;
  }, []);
  const hashes = useMemo(async () => {
    return {
      city: geoLocation?.city && (await hash(geoLocation?.city.toLowerCase())),
      region: geoLocation?.countryRegion && (await hash(geoLocation?.countryRegion.toLowerCase())),
      country: geoLocation?.country && (await hash(geoLocation?.country.toLowerCase()))
    };
  }, [geoLocation?.city, geoLocation?.country, geoLocation?.countryRegion]);

  // listen to user properties
  useIdentify({
    'site country': locale.countryCodeLowercase,
    'site language': locale.lang,
    hashes,
    'user agent': userAgent,
    'first visit time': firstVisit?.timestamp,
    'first visit id': firstVisit?.id,
    'membership state': user?.userMembershipState,
    roles: user?.roles,
    entitlements: user?.entitlements,
    'auth status': user?.guid ? 'logged in' : 'logged out',
    'user agent data': userAgentData ?? undefined,
    'anonymous id': anonymousId ?? undefined
  });
  useDigitalIdentify();
}

/**
 * Returns object of common properties used across all Amplitude page view events.
 */
function useScreenViewProperties() {
  const $ = _c(5);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams ? searchParams?.toString() : undefined;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchParamsString = t0;
  const origin = getIsServer() ? configPublic.general.siteMapUrl : window.location.origin;
  let t1;
  if ($[2] !== pathname || $[3] !== searchParamsString) {
    const url = new URL(`${origin}${pathname}${searchParamsString && "?" + searchParamsString}`);
    t1 = getPageURLProperties(url.href);
    $[2] = pathname;
    $[3] = searchParamsString;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Track a generic Builder page view.
 *
 * @param pageType The value of the page's pageType field in Builder.
 * @param properties The value of the page's analyticsData field in Builder.
 */
export function useViewBuilderPage(pageType?: string, properties?: Record<string, any>) {
  const screenViewProperties = useScreenViewProperties();
  const {
    live
  } = getBuilderPreviewMode();

  // We want to ensure pageType defaults to 'other', even if it's an empty string

  pageType = pageType?.toLowerCase() || 'other';
  const isCartPage = pageType === 'cart';
  useEffectOnce(function () {
    if (!live) return;
    ampli.viewCartPage({
      ...screenViewProperties,
      'line items': []
    });
  }, [], Boolean(isCartPage));
  useEffectOnce(function () {
    if (!live) return;
    ampli.track({
      event_type: `view ${pageType} page`,
      event_properties: {
        ...screenViewProperties,
        ...properties
      }
    });
  }, [], !isCartPage);
}

/**
 * Track the submission of a user's email address.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSubmitEmailAddress() {
  const {
    live
  } = getBuilderPreviewMode();
  return async function (properties: Pick<SubmitEmailAddressProperties, 'submit email result'> & {
    email: string;
  }) {
    if (!live) return;
    const {
      email,
      ...rest
    } = properties;
    // Send event to Amplitude
    ampli.submitEmailAddress({
      ...getPageGlobalProps(location.href),
      ...rest
    });
    ampli.flush();

    // Send event to Google Tag Manager
    const emailHashed = email && (await hash(email));
    window.dataLayer.push({
      event: 'email_confirmed',
      alt_customer_id: emailHashed,
      email_result: properties['submit email result']
    });
  };
}

/**
 * Track the submission of the create account form.
 */
export function useTrackCreateAccount() {
  const {
    live
  } = getBuilderPreviewMode();
  return function () {
    if (!live) return;
    ampli.createAccount({
      ...getPageGlobalProps(location.href)
    });
  };
}

/**
 * Track the setting of a password for new users who have completed a guest checkout.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSetPassword() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<SetPasswordProperties, 'error code' | 'result'>) {
    if (!live) return;
    ampli.setPassword({
      ...getPageGlobalProps(location.href),
      ...properties
    });
    return ampli.flush();
  };
}

/**
 * Track when a generic CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectCta() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<SelectCtaProperties, 'cta url' | 'module'>) {
    if (!live) return;
    ampli.selectCta({
      ...getPageGlobalProps(location.href),
      ...properties
    });
  };
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectNavCta() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<SelectNavCtaProperties, 'cta url' | 'module'>) {
    if (!live) return;
    ampli.selectNavCta({
      ...getPageGlobalProps(location.href),
      ...properties
    });
    return ampli.flush();
  };
}

/**
 * Track when a user chooses to be assigned to a partner from the lead wheel.
 */
export function useTrackEnterLeadWheel() {
  const {
    live
  } = getBuilderPreviewMode();
  return function () {
    if (!live) return;
    ampli.enterLeadWheel({
      ...getPageGlobalProps(location.href)
    });
  };
}

/**
 * Tracks when a user adds a promo code during checkout.
 */
export function useTrackAddPromoCode() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<AddPromoCodeProperties, 'promo code' | 'promo code result'>) {
    if (!live) return;
    ampli.addPromoCode({
      ...getPageGlobalProps(location.href),
      ...properties
    });
  };
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectStickyFooterCta() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<SelectStickyFooterCtaProperties, 'cta url' | 'module'>) {
    if (!live) return;
    ampli.selectStickyFooterCta({
      ...getPageGlobalProps(location.href),
      ...properties
    });
    return ampli.flush();
  };
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectPencilBannerCta() {
  const {
    live
  } = getBuilderPreviewMode();
  return function (properties: Pick<SelectPencilBannerCtaProperties, 'cta url' | 'module'>) {
    if (!live) return;
    ampli.selectPencilBannerCta({
      ...getPageGlobalProps(location.href),
      ...properties
    });
    return ampli.flush();
  };
}

/**
 * Track when a CTA is clicked in a rail component.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectRailCta() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getBuilderPreviewMode();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    live
  } = t0;
  const railItem = useRailItemContext();
  const slug = railItem?.slug;
  const position = railItem?.position;
  let t1;
  if ($[1] !== position || $[2] !== slug) {
    t1 = function (properties) {
      if (!live || !slug) {
        return;
      }
      ampli.selectRailCta({
        ...getPageGlobalProps(location.href),
        ...properties,
        "rail slug": slug,
        "cta position": position ?? 0
      });
      return ampli.flush();
    };
    $[1] = position;
    $[2] = slug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/**
 * Used to send legacy utag/tealium events from SSE to Amplitude.
 */
type UtagLinkArgs = {
  tealium_event?: string;
  link_tracking?: string;
} & Record<string, any>;
export function trackUtagEvent(args?: UtagLinkArgs) {
  const {
    tealium_event,
    link_tracking,
    ...rest
  } = args ?? {};
  const eventName = link_tracking?.replace('_', ' ') ?? tealium_event?.toLowerCase() ?? 'unknown';
  ampli.track({
    event_type: `sse: ${eventName}`,
    event_properties: rest
  });
}

/**
 * Tracks when a user opens the chat window.
 */
export function trackOpenChat(isNewConversation: boolean) {
  getActiveAmpli(location.pathname).openChat({
    'is new conversation': isNewConversation,
    ...getPageGlobalProps(location.href)
  });
}

/**
 * Tracks when a user starts a chat conversation.
 */
export function trackStartConversation(conversationId: string) {
  getActiveAmpli(location.pathname).startConversation({
    'conversation id': conversationId,
    ...getPageGlobalProps(location.href)
  });
}

/**
 * Track the community page.
 * @param properties Relevant event properties.
 */
export function useViewCommunityPage(slView) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getBuilderPreviewMode();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    live
  } = t0;
  const screenViewProperties = useScreenViewProperties();
  let t1;
  let t2;
  if ($[1] !== screenViewProperties || $[2] !== slView) {
    t1 = () => {
      if (!live) {
        return;
      }
      ampli.viewCommunityPage({
        ...screenViewProperties,
        section: String(slView)
      });
    };
    t2 = [live, screenViewProperties, slView];
    $[1] = screenViewProperties;
    $[2] = slView;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
}